// viewport.ts — is this a phone?
//
// One question, asked in one place, so "mobile" means the same thing
// everywhere rather than a breakpoint guessed per component.
//
// A WIDTH, not a user-agent sniff. A narrow window on a laptop should get the
// stacked layout too — it is genuinely better at that size — and sniffing has
// been wrong about every new device category since it was invented.
import { useEffect, useState } from 'react'

/**
 * Below this, the three-pane shell stops being usable: the rail, the thread and
 * the context pane cannot share a phone's width, and squeezing them produces a
 * layout nobody wants on either device.
 */
export const MOBILE_MAX_WIDTH = 768

export const MOBILE_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`

/**
 * The three-pane layout renders above MOBILE_MAX_WIDTH, but between there and a
 * roomy desktop — a phone held in landscape (~900px), a small tablet — the
 * context pane crowds the thread. Below this width the pane therefore starts
 * COLLAPSED by default (the person can still open it, and that choice persists
 * via the `p.ctx` preference); above it, open as before. A separate, higher
 * line than MOBILE_MAX_WIDTH: at 800px you still get three panes, just with the
 * context one tucked away until asked for.
 */
export const CONTEXT_DEFAULT_MIN_WIDTH = 1100

export const CONTEXT_DEFAULT_QUERY = `(min-width: ${CONTEXT_DEFAULT_MIN_WIDTH}px)`

/**
 * Whether the context pane should default to OPEN for this viewport — the
 * fallback used only when the person has no saved `p.ctx` preference yet
 * (see bootstrap.ts). SSR/jsdom with no matchMedia keeps the historical
 * open-by-default, exactly as `matches()` treats a missing matchMedia as
 * desktop elsewhere in this file.
 */
export function defaultCtxOpenForWidth(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(CONTEXT_DEFAULT_QUERY).matches
    : true
}

/** SSR- and jsdom-safe: no matchMedia means a desktop, which is the old behaviour. */
function matches(query: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false
}

/**
 * Whether the stacked, phone-shaped layout should be used.
 *
 * Live, not read once: rotating a phone or dragging a window across the
 * breakpoint has to switch layouts, and a value captured at mount would leave
 * somebody in the wrong one until a reload.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => matches(MOBILE_QUERY))

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(MOBILE_QUERY)
    const onChange = (e: MediaQueryListEvent): void => setIsMobile(e.matches)
    // Re-read on subscribe: the query can have changed between the initial
    // state and this effect running.
    setIsMobile(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  return isMobile
}
